/**
 * Simplified sound manager using Web Audio API
 */

class SoundManager {
  private audioContext: AudioContext | null = null;
  private masterGainNode: GainNode | null = null;
  private buffers: Map<string, AudioBuffer> = new Map();
  private loadingPromises: Map<string, Promise<AudioBuffer>> = new Map();
  private muted: boolean = false;
  private initialized: boolean = false;
  private playingSounds: Set<AudioBufferSourceNode> = new Set();

  constructor() {
    // Load mute state from localStorage
    const savedMuteState = localStorage.getItem("soundMuted");
    this.muted = savedMuteState === "true";

    // Initialize audio context immediately
    this.initAudioContext();
  }

  /**
   * Initialize AudioContext on first user interaction
   */
  private async initAudioContext(): Promise<void> {
    if (this.initialized) return;

    try {
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) {
        console.error("Web Audio API not supported");
        return;
      }

      this.audioContext = new AudioContextClass();

      // Create master gain node with reduced default volume to prevent clipping
      this.masterGainNode = this.audioContext.createGain();
      this.masterGainNode.gain.setValueAtTime(
        this.muted ? 0 : 0.7,
        this.audioContext.currentTime
      );
      this.masterGainNode.connect(this.audioContext.destination);

      // Resume if suspended
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }

      this.initialized = true;
    } catch (error) {
      console.error("Failed to initialize AudioContext:", error);
    }
  }

  /**
   * Load and decode audio buffer
   */
  private async loadAudioBuffer(url: string): Promise<AudioBuffer> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();

      if (!this.audioContext) {
        await this.initAudioContext();
      }

      if (!this.audioContext) {
        throw new Error("AudioContext not available");
      }

      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      return audioBuffer;
    } catch (error) {
      console.error(`Failed to load audio: ${url}`, error);
      throw error;
    }
  }

  /**
   * Preload a sound file
   */
  preload(name: string, url: string): void {
    if (this.buffers.has(name) || this.loadingPromises.has(name)) {
      return;
    }

    const loadPromise = this.loadAudioBuffer(url)
      .then((buffer) => {
        this.buffers.set(name, buffer);
        this.loadingPromises.delete(name);
        return buffer;
      })
      .catch((error) => {
        console.error(`Failed to preload ${name}:`, error);
        this.loadingPromises.delete(name);
        throw error;
      });

    this.loadingPromises.set(name, loadPromise);
  }

  /**
   * Initialize all game sounds
   */
  init(): void {
    // Preload essential sounds
    this.preload("coin", "/sounds/coin.wav");
    this.preload("click", "/sounds/click.wav");
    this.preload("victory", "/sounds/victory.wav");
    this.preload("whoosh", "/sounds/whoosh.wav");
    this.preload("whoosh_in", "/sounds/whoosh_in.wav");
    this.preload("whoosh_out", "/sounds/whoosh_out.wav");
    this.preload("bet_small", "/sounds/bet_small.wav");
    this.preload("bet_med", "/sounds/bet_med.wav");
    this.preload("bet_large", "/sounds/bet_large.wav");
    this.preload("impact_small", "/sounds/impact_small.wav");
    this.preload("impact_med", "/sounds/impact_med.wav");
    this.preload("impact_large", "/sounds/impact_large.wav");

    // Preload background music
    this.preload("bgm", "/sounds/bgm.ogg");
    this.preload("battleloop", "/sounds/battleloop.ogg");

    // Preload victory sounds
    this.preload("chargeup", "/sounds/chargeup.wav");
    this.preload("blasted", "/sounds/blasted.wav");
    this.preload("winner", "/sounds/winner.ogg");
  }

  /**
   * Play a sound by name
   */
  async play(
    name: string,
    volume: number = 1.0,
    loop: boolean = false
  ): Promise<void> {
    if (this.muted) return;

    if (!this.audioContext || !this.masterGainNode) {
      return;
    }

    try {
      // Get the buffer
      let buffer: AudioBuffer | null = null;

      if (this.loadingPromises.has(name)) {
        buffer = await this.loadingPromises.get(name)!;
      } else if (this.buffers.has(name)) {
        buffer = this.buffers.get(name)!;
      }

      if (!buffer) {
        console.warn(`Sound "${name}" not loaded`);
        return;
      }

      // Create source and gain nodes
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.loop = loop;

      const gainNode = this.audioContext.createGain();

      // Clamp volume to safe range and apply logarithmic scaling for more natural perception
      const clampedVolume = Math.max(0, Math.min(1, volume));
      // Convert linear volume to logarithmic scale (more natural for human hearing)
      const logarithmicVolume = clampedVolume * clampedVolume;

      // Use setValueAtTime for glitch-free volume setting
      gainNode.gain.setValueAtTime(
        logarithmicVolume,
        this.audioContext.currentTime
      );

      // Connect nodes
      source.connect(gainNode);
      gainNode.connect(this.masterGainNode);

      // Track playing sound
      this.playingSounds.add(source);

      // Clean up when finished
      source.onended = () => {
        try {
          source.disconnect();
          gainNode.disconnect();
        } catch (e) {
          // Already disconnected
        }
        this.playingSounds.delete(source);
      };

      // Play the sound
      source.start(0);
    } catch (error) {
      console.error(`Error playing sound "${name}":`, error);
    }
  }

  /**
   * Play background music (loops automatically)
   */
  async playBackgroundMusic(name: string, volume: number = 0.5): Promise<void> {
    await this.play(name, volume, true);
  }

  /**
   * Stop background music
   */
  stopBackgroundMusic(): void {
    this.stopAll();
  }

  /**
   * Toggle mute state
   */
  toggleMute(): boolean {
    this.muted = !this.muted;
    localStorage.setItem("soundMuted", String(this.muted));

    if (this.masterGainNode && this.audioContext) {
      const now = this.audioContext.currentTime;
      this.masterGainNode.gain.cancelScheduledValues(now);
      this.masterGainNode.gain.setValueAtTime(
        this.masterGainNode.gain.value,
        now
      );
      this.masterGainNode.gain.linearRampToValueAtTime(
        this.muted ? 0 : 0.7,
        now + 0.05
      );
    }

    // Stop all sounds when muting
    if (this.muted) {
      this.stopAll();
    }

    return this.muted;
  }

  /**
   * Get current mute state
   */
  isMuted(): boolean {
    return this.muted;
  }

  /**
   * Set mute state
   */
  setMuted(muted: boolean): void {
    if (this.muted === muted) return;

    this.muted = muted;
    localStorage.setItem("soundMuted", String(muted));

    if (this.masterGainNode && this.audioContext) {
      const now = this.audioContext.currentTime;
      this.masterGainNode.gain.cancelScheduledValues(now);
      this.masterGainNode.gain.setValueAtTime(
        this.masterGainNode.gain.value,
        now
      );
      this.masterGainNode.gain.linearRampToValueAtTime(
        muted ? 0 : 0.7,
        now + 0.05
      );
    }

    // Stop all sounds when muting
    if (muted) {
      this.stopAll();
    }
  }

  /**
   * Stop all sounds
   */
  stopAll(): void {
    for (const source of this.playingSounds) {
      try {
        source.stop();
      } catch (e) {
        // Already stopped
      }
    }
    this.playingSounds.clear();
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.stopAll();
    this.buffers.clear();
    this.loadingPromises.clear();

    if (this.masterGainNode) {
      try {
        this.masterGainNode.disconnect();
      } catch (e) {
        // Already disconnected
      }
    }

    if (this.audioContext && this.audioContext.state !== "closed") {
      try {
        this.audioContext.close();
      } catch (e) {
        // Already closed
      }
    }

    this.audioContext = null;
    this.masterGainNode = null;
    this.initialized = false;
  }
}

// Export singleton instance
export const soundManager = new SoundManager();

// Export sound names as constants for type safety
export const SOUNDS = {
  COIN: "coin",
  CLICK: "click",
  IMPACT: "impact",
  VICTORY: "victory",
  WHOOSH: "whoosh",
  WHOOSH_IN: "whoosh_in",
  WHOOSH_OUT: "whoosh_out",
  BET_SMALL: "bet_small",
  BET_MED: "bet_med",
  BET_LARGE: "bet_large",
  IMPACT_SMALL: "impact_small",
  IMPACT_MED: "impact_med",
  IMPACT_LARGE: "impact_large",
  BGM: "bgm",
  BATTLELOOP: "battleloop",
  CHARGEUP: "chargeup",
  BLASTED: "blasted",
  WINNER: "winner",
} as const;

export type SoundName = (typeof SOUNDS)[keyof typeof SOUNDS];
